// Currency formatting utilities

/**
 * Format a number as currency
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'USD')
 * @param {string} locale - Locale (default: 'en-US')
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = "USD", locale = "en-US") {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "$0.00";
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    // Fallback formatting
    return `$${parseFloat(amount).toFixed(2)}`;
  }
}

/**
 * Format a number as a plain number with commas
 * @param {number} number - Number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @param {string} locale - Locale (default: 'en-US')
 * @returns {string} Formatted number string
 */
export function formatNumber(number, decimals = 0, locale = "en-US") {
  if (number === null || number === undefined || isNaN(number)) {
    return "0";
  }

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(number);
  } catch (error) {
    // Fallback formatting
    return parseFloat(number).toFixed(decimals);
  }
}

/**
 * Format hours with 1 decimal place
 * @param {number} hours - Hours to format
 * @returns {string} Formatted hours string
 */
export function formatHours(hours) {
  if (hours === null || hours === undefined || isNaN(hours)) {
    return "0.0";
  }

  return parseFloat(hours).toFixed(1);
}

/**
 * Format rate (currency per hour)
 * @param {number} rate - Rate to format
 * @returns {string} Formatted rate string
 */
export function formatRate(rate) {
  if (rate === null || rate === undefined || isNaN(rate)) {
    return "$0.00/hr";
  }

  return `${formatCurrency(rate)}/hr`;
}

/**
 * Calculate percentage
 * @param {number} part - Part value
 * @param {number} total - Total value
 * @param {number} decimals - Decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export function formatPercentage(part, total, decimals = 1) {
  if (total === 0 || isNaN(part) || isNaN(total)) {
    return "0%";
  }

  const percentage = (part / total) * 100;
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string to parse
 * @returns {number} Parsed number or 0
 */
export function parseCurrency(currencyString) {
  if (!currencyString) return 0;

  try {
    // Remove currency symbols, commas, and whitespace
    const cleaned = currencyString.replace(/[^\d.-]/g, "").trim();

    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  } catch (error) {
    return 0;
  }
}

/**
 * Calculate net pay from gross and deductions
 * @param {number} gross - Gross amount
 * @param {number} deductions - Deductions amount
 * @returns {number} Net amount
 */
export function calculateNet(gross, deductions) {
  const grossNum = parseFloat(gross) || 0;
  const deductionsNum = parseFloat(deductions) || 0;
  return Math.max(0, grossNum - deductionsNum);
}

/**
 * Calculate total from array of amounts
 * @param {Array} amounts - Array of amounts
 * @param {string} key - Optional key if amounts are objects
 * @returns {number} Total amount
 */
export function calculateTotal(amounts, key = null) {
  if (!Array.isArray(amounts)) return 0;

  return amounts.reduce((total, item) => {
    const amount = key ? parseFloat(item[key]) || 0 : parseFloat(item) || 0;
    return total + amount;
  }, 0);
}

/**
 * Format large numbers with K, M, B suffixes
 * @param {number} num - Number to format
 * @returns {string} Formatted number with suffix
 */
export function formatLargeNumber(num) {
  if (num === null || num === undefined || isNaN(num)) {
    return "0";
  }

  const absNum = Math.abs(num);

  if (absNum >= 1000000000) {
    return (num / 1000000000).toFixed(1) + "B";
  }
  if (absNum >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (absNum >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }

  return num.toString();
}

/**
 * Round to specified decimal places
 * @param {number} value - Value to round
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {number} Rounded value
 */
export function roundTo(value, decimals = 2) {
  const factor = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * Format duration in milliseconds to human readable format
 * @param {number} durationMs - Duration in milliseconds
 * @returns {string} Formatted duration
 */
export function formatDuration(durationMs) {
  if (!durationMs || durationMs < 0) return "0s";

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  } else if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${seconds}s`;
  }
}
